import { PermissionOperation, PermissionResource } from 'generated/graphql';
import AddIcon from '@mui/icons-material/Add';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { DashboardListItemViewFragment } from 'generated/graphql';
import { useNavigate } from 'react-router-dom';
import { NAV_PATHS } from '@modules/common/constants';
import { CreateNewDashboardTemplateList } from './CreateNewDashboardTemplateList';
import { CanAccess } from '@modules/common/components/CanAccess';
import { SettingsRounded } from '@mui/icons-material';
import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';

interface CreateNewDashboardMenuProps {
  templates: DashboardListItemViewFragment[];
  isLoadingTemplates: boolean;
  selectedTemplate?: DashboardListItemViewFragment;
  onTemplateSelect: (template: DashboardListItemViewFragment) => void;
}

export const CreateNewDashboardMenu = ({
  templates,
  isLoadingTemplates,
  selectedTemplate,
  onTemplateSelect,
}: CreateNewDashboardMenuProps) => {
  const navigate = useNavigate();

  const onCreateNewDashboard = () => {
    const draftDashboardRoute = `/${NAV_PATHS.NEW_DASHBOARD}`;
    navigate(draftDashboardRoute);
  };

  const navigateToDashboardSettings = () => {
    navigate(`/${NAV_PATHS.DASHBOARD_SETTINGS}`);
  };

  const canManageTemplates = useIsAuthorized({
    resource: PermissionResource.Template,
    operation: PermissionOperation.Write,
  });

  const isLargeScreen = useMediaQuery('(min-width:1110px)');
  const isMidScreen = useMediaQuery('(min-width:1040px)');

  return (
    <Box width="100%">
      <Stack spacing={1} pt={4}>
        <Typography variant="caption">Start from scratch</Typography>
        <CanAccess
          resource={PermissionResource.Artefact}
          operation={PermissionOperation.Write}
        >
          <LoadingButton
            variant="outlined"
            onClick={() => onCreateNewDashboard()}
            size="large"
            fullWidth
            startIcon={<AddIcon />}
          >
            New {isLargeScreen && 'Blank'} {isMidScreen && 'Dashboard'}
          </LoadingButton>
        </CanAccess>
      </Stack>
      <Stack spacing={1} pt={8}>
        <Stack direction="row" alignItems="center" spacing={0.2}>
          <Typography variant="caption">Choose a template</Typography>
          {canManageTemplates && (
            <Tooltip arrow title="Edit Templates" placement="top">
              <IconButton
                color="primary"
                size="small"
                onClick={navigateToDashboardSettings}
              >
                <SettingsRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <CreateNewDashboardTemplateList
          templates={templates}
          isLoadingTemplates={isLoadingTemplates}
          selectedTemplate={selectedTemplate}
          onTemplateSelect={onTemplateSelect}
        />
      </Stack>
    </Box>
  );
};
